import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Polls /api/admin/ops-health every `intervalMs` (default 60 s).
 *
 * Returns `{ data, counts, total, refresh, loading }`.
 *
 * Used by:
 *  - OpsHealthWidget on the dashboard (initial load + manual refresh)
 *  - OpsCriticalBadge in the Admin header (sticky critical count)
 *
 * Why a custom hook (not React Query): the rest of the codebase uses raw
 * axios for HTTP + this is the only polled endpoint we have so far. Keeping
 * the dep tree minimal.
 */
export function useOpsHealth(intervalMs = 60_000) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchOnce = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/ops-health`);
            setData(data);
        } catch {
            // Swallow — the badge / widget should NEVER take down the page.
            // 403 for users without view_dashboard perm is normal.
            setData({ items: [], counts: { critical: 0, warn: 0, info: 0 }, total: 0 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOnce();
        const id = setInterval(fetchOnce, intervalMs);
        return () => clearInterval(id);
    }, [fetchOnce, intervalMs]);

    return { data, counts: data?.counts || {}, total: data?.total || 0, refresh: fetchOnce, loading };
}
